import clsx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { addDays, fmtDateWeek, fmtMonthDay, type FollowUpDTO, type ReportItemDTO, type ReportPageDTO } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { serverNow, timeCN } from '../../lib/clock';
import { useHotkeys } from '../../lib/hotkeys';
import type { Layout } from '../../lib/media';
import { useProjects } from '../../lib/queries';
import { useTheme } from '../../lib/theme';
import { Icon, StatusIcon } from '../../ui/icons';
import { AIAvatar, Avatar, Button, GroupLabel, Hint, IconButton, Kbd, Kbds, KV, Message, Readout, WithVague } from '../../ui/primitives';
import { durationText, itemMeta, type ReportEditor } from './useReportEditor';
import { ComposerAttach, ItemFiles } from './ItemFiles';

export function ReportDesktop({ report, ed, layout, today }: { report: ReportPageDTO; ed: ReportEditor; layout: Layout; today: string }) {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme();
  const [sel, setSel] = useState(-1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingPlan, setAddingPlan] = useState(false);
  const composerRef = useRef<HTMLInputElement>(null);
  const isToday = report.date === today;
  const items = report.items;

  const followUpByItem = useMemo(() => {
    const m = new Map<string, FollowUpDTO>();
    for (const f of report.aiFollowUps) if (f.reportItemId) m.set(f.reportItemId, f);
    return m;
  }, [report.aiFollowUps]);

  const choices = (f: FollowUpDTO | null) => (f ? f.suggestedAnswers.slice(0, 2) : []);
  const pick = (f: FollowUpDTO, n: number) => {
    const list = choices(f);
    if (n <= list.length) void ed.answer(f, list[n - 1]);
    else composerRef.current?.focus();
  };

  useHotkeys(
    {
      j: () => navigate(`/reports/${addDays(report.date, -1)}`),
      k: () => isToday || navigate(addDays(report.date, 1) === today ? '/' : `/reports/${addDays(report.date, 1)}`),
      ArrowDown: () => setSel((s) => Math.min(s + 1, items.length - 1)),
      ArrowUp: () => setSel((s) => Math.max(s - 1, 0)),
      e: () => ed.editable && sel >= 0 && setEditingId(items[sel]?.id ?? null),
      Enter: () => ed.editable && sel >= 0 && setEditingId(items[sel]?.id ?? null),
      p: () => ed.editable && setAddingPlan(true),
      '1': () => ed.replyTarget && pick(ed.replyTarget, 1),
      '2': () => ed.replyTarget && pick(ed.replyTarget, 2),
      '3': () => ed.replyTarget && pick(ed.replyTarget, 3),
      'mod+Enter': () => ed.editable && ed.hasContent && void ed.submit(),
    },
    true,
  );

  const showPanel = layout === 'full';
  const hasManagerAsk = ed.pendingManager.length > 0;
  const prefillLabel =
    report.prefillSource === 'plan' && report.prefillFromDate
      ? `AI 已按 ${fmtMonthDay(report.prefillFromDate)} 的明日计划预填`
      : report.recent.length === 0
        ? '第一份日报'
        : '没有可预填的计划';

  return (
    <>
      <TopBar crumbs={['我的日报', fmtMonthDay(report.date)]} icon="doc">
        <span className="t-meta" style={{ marginRight: 12 }}>
          {saveLabel(ed)}
        </span>
        <IconButton name="chevL" label="前一天（J）" onClick={() => navigate(`/reports/${addDays(report.date, -1)}`)} />
        <IconButton
          name="chevR"
          label="后一天（K）"
          disabled={isToday}
          onClick={() => navigate(addDays(report.date, 1) === today ? '/' : `/reports/${addDays(report.date, 1)}`)}
        />
        <span className="wd-vsep" />
        <IconButton name={theme === 'dark' ? 'sun' : 'moon'} label={theme === 'dark' ? '切换到浅色' : '切换到深色'} onClick={toggleTheme} />
        <IconButton name="plane" label="请假 / 出差" onClick={() => navigate('/me?leave=1')} />
      </TopBar>

      {report.overdue && (
        <div className="wd-banner">
          <Icon name="clock" size={14} />
          <span className="grow">今天的日报还没提交，不确定的地方可以先提交，明天再补</span>
          <span className="t-meta">已过截止 {durationText(-(ed.minutesLeft ?? 0))}</span>
        </div>
      )}
      {!isToday && (
        <div className="wd-banner">
          <Icon name="cal" size={14} />
          <span className="grow">这是 {fmtDateWeek(report.date)} 的日报，只读</span>
          <Link to="/" className="wd-btn text">
            回到今天
          </Link>
        </div>
      )}

      <div className="rp-body">
        <div className={clsx('rp-main', hasManagerAsk && 'narrow')}>
          <div className="rp-inner">
            <div className="rp-scroll">
            <div className="rp-head">
              <div>
                <div className="t-page-title">{fmtDateWeek(report.date)}</div>
                <div className="rp-meta">
                  {report.prefillSource === 'plan' && <span className="rp-dot" />}
                  <span>{prefillLabel}</span>
                  <span>·</span>
                  <span>{items.length} 个项目</span>
                  <span>·</span>
                  <span>{report.submittedAt ? `${timeCN(report.submittedAt)} 提交` : timeCN(report.updatedAt)}</span>
                </div>
              </div>
              {report.status === 'draft' && isToday && !hasManagerAsk && (
                <div className="rp-clock">
                  <span className={clsx('t-meta', report.overdue && 'c-red')}>{report.deadline} 截止</span>
                  <span className="big">{timeCN(serverNow())}</span>
                </div>
              )}
            </div>

            {hasManagerAsk ? (
              <div style={{ marginTop: 28 }}>
                <GroupLabel right={<span>{ed.pendingManager.length}</span>}>来自{ed.pendingManager[0].fromName}的追问</GroupLabel>
                {ed.pendingManager.map((f) => (
                  <ManagerAsk key={f.id} f={f} ed={ed} highlight={f.id === ed.replyTarget?.id} onWrite={() => composerRef.current?.focus()} />
                ))}
              </div>
            ) : (
              report.readouts.length > 0 && (
                <div className="wd-readouts" style={{ marginTop: 32, gridTemplateColumns: `repeat(${Math.max(report.readouts.length, 1)}, minmax(0, 1fr))` }}>
                  {report.readouts.map((p) => (
                    <Readout key={p.id} p={p} today={today} />
                  ))}
                </div>
              )
            )}

            <div style={{ marginTop: hasManagerAsk ? 24 : 32 }}>
              <GroupLabel right={<><span>{items.length}</span><span className="flex1" />{ed.editedCount > 0 && <span>你修改了 {ed.editedCount} 处</span>}</>}>
                今日进展
              </GroupLabel>
            </div>
            <div className="rp-rows">
              {items.length === 0 && <div className="rp-note">你还没有负责的项目：可以直接在下面的补充说明里写今天做了什么并提交；去「我 › 负责的项目」加入项目后，日报会按项目预填。</div>}
              {items.map((it, i) => {
                const f = followUpByItem.get(it.id);
                return (
                  <div key={it.id}>
                    {i > 0 && <div className="rp-sep" />}
                    <ItemRow
                      item={it}
                      ed={ed}
                      followUp={f}
                      selected={sel === i}
                      editing={editingId === it.id}
                      onSelect={() => setSel(i)}
                      onEdit={() => ed.editable && setEditingId(it.id)}
                      onDone={() => setEditingId(null)}
                    />
                    <ItemFiles item={it} editable={ed.editable} showAdd={sel === i} />
                    {f && !f.skipped && <AIAsk f={f} ed={ed} highlight={f.id === ed.replyTarget?.id} onWrite={() => composerRef.current?.focus()} />}
                  </div>
                );
              })}
            </div>

            {(ed.editable || report.plans.length > 0) && (
              <Plans report={report} ed={ed} adding={addingPlan} setAdding={setAddingPlan} />
            )}

            {report.note && !ed.editable && (
              <div style={{ marginTop: 24 }}>
                <GroupLabel>补充说明</GroupLabel>
                <div className="t-body" style={{ whiteSpace: 'pre-wrap' }}>
                  {report.note}
                </div>
              </div>
            )}

            </div>
            <div className="rp-dock">
            {ed.notice && <div className="rp-note">{ed.notice}</div>}
            {ed.error && (
              <div className="rp-error" role="alert">
                {ed.error}
              </div>
            )}

            {ed.editable ? (
              <div className="rp-composer" style={{ marginTop: 12 }}>
                {ed.replyTarget && (
                  <span className="who">{ed.replyTarget.kind === 'manager' ? `回复${ed.replyTarget.fromName}` : `回复 ${ed.replyTarget.projectCode}`}</span>
                )}
                <input
                  ref={composerRef}
                  value={ed.replyTarget ? ed.reply : ed.note}
                  onChange={(e) => (ed.replyTarget ? ed.setReply(e.target.value) : ed.setNote(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      if (e.metaKey || e.ctrlKey) {
                        if (ed.replyTarget && ed.reply.trim()) void ed.sendReply();
                        else void ed.submit();
                      } else if (ed.replyTarget && ed.reply.trim()) void ed.sendReply();
                    }
                    if (e.key === 'Escape') (e.target as HTMLInputElement).blur();
                  }}
                  placeholder={ed.replyTarget ? (choices(ed.replyTarget).length ? `按 1–${choices(ed.replyTarget).length + 1} 选择，或直接输入…` : '直接输入回复…') : '补充说明（可选）…'}
                  aria-label={ed.replyTarget ? '回复追问' : '补充说明'}
                />
                <ComposerAttach items={report.items} />
                <span className="wd-vsep" style={{ margin: 0 }} />
                <Kbds keys={['⌘', '↵']} />
                <Button variant="primary" onClick={() => void ed.submit()} disabled={!ed.hasContent || ed.actions.submit.isPending}>
                  {ed.actions.submit.isPending ? '提交中…' : '提交日报'}
                </Button>
              </div>
            ) : report.status === 'submitted' ? (
              <div className="rp-done-line">
                <StatusIcon status="done" />
                <span>
                  已提交 · {report.submittedAt ? timeCN(report.submittedAt) : ''}
                  {report.late ? ' · 逾期提交' : ''}
                </span>
                <span className="flex1" />
                {report.canRetract && (
                  <Button variant="text" onClick={() => void ed.retract()}>
                    撤回修改
                  </Button>
                )}
              </div>
            ) : null}
            </div>
          </div>
        </div>

        {showPanel && <PropertyPanel report={report} ed={ed} isToday={isToday} showDeadline={hasManagerAsk} />}
      </div>
    </>
  );
}

function saveLabel(ed: ReportEditor) {
  if (!ed.editable) return '';
  switch (ed.saveState) {
    case 'saving':
      return '保存中…';
    case 'offline':
      return '网络断了，联网后自动保存';
    case 'error':
      return '这条没有保存，改好后会自动保存';
    default:
      return '已自动保存';
  }
}

// ---------------------------------------------------------------------------

function ItemRow({
  item,
  ed,
  followUp,
  selected,
  editing,
  onSelect,
  onEdit,
  onDone,
}: {
  item: ReportItemDTO;
  ed: ReportEditor;
  followUp: FollowUpDTO | undefined;
  selected: boolean;
  editing: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDone: () => void;
}) {
  const text = ed.textOf(item);
  const meta = itemMeta(item, text, followUp);
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [editing, text]);
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.setSelectionRange(ref.current.value.length, ref.current.value.length);
    }
  }, [editing]);

  if (editing) {
    return (
      <div className="rp-row editing">
        <StatusIcon status={item.projectStatus} />
        <span className="code">{item.projectCode}</span>
        <textarea
          ref={ref}
          className="rp-edit"
          value={text}
          rows={1}
          placeholder={`今天在 ${item.projectName} 上做了什么？`}
          onChange={(e) => ed.setItemText(item.id, e.target.value)}
          onBlur={() => {
            void ed.flush();
            onDone();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              ed.revert(item.id);
              onDone();
            } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || !e.shiftKey) && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void ed.flush();
              onDone();
            }
          }}
          aria-label={`${item.projectName}的今日进展`}
        />
        <span className="right" style={{ lineHeight: '20px' }}>
          <Kbd>Esc</Kbd>
        </span>
      </div>
    );
  }
  return (
    <div
      className={clsx('rp-row', selected && 'sel', ed.editable && 'clickable')}
      onMouseEnter={onSelect}
      onClick={onEdit}
      title={ed.editable ? '点击编辑' : undefined}
    >
      <StatusIcon status={item.projectStatus} />
      <span className="code">{item.projectCode}</span>
      <span className={clsx('text', !text.trim() && 'empty')}>
        {text.trim() ? <WithVague text={text} phrase={item.vaguePhrase} /> : `今天在 ${item.projectName} 上做了什么？`}
      </span>
      {meta && <span className="right" style={{ color: meta.tone === 3 ? 'var(--text-3)' : undefined }}>{meta.label}</span>}
    </div>
  );
}

function Choices({ f, ed, onWrite, writeLabel }: { f: FollowUpDTO; ed: ReportEditor; onWrite: () => void; writeLabel: string }) {
  const list = f.suggestedAnswers.slice(0, 2);
  return (
    <div className="rp-choices">
      {list.map((a, i) => (
        <button key={a} type="button" className={clsx('rp-choice', i === 0 && 'hl')} onClick={() => void ed.answer(f, a)}>
          <Kbd>{i + 1}</Kbd>
          <span>{a}</span>
        </button>
      ))}
      <button type="button" className="rp-choice write" onClick={onWrite}>
        <Kbd>{list.length + 1}</Kbd>
        <span>{writeLabel}</span>
      </button>
    </div>
  );
}

function AIAsk({ f, ed, highlight, onWrite }: { f: FollowUpDTO; ed: ReportEditor; highlight: boolean; onWrite: () => void }) {
  return (
    <div className="rp-ask" style={highlight ? undefined : { opacity: f.answer ? 1 : 0.85 }}>
      <div className="row" style={{ gap: 8 }}>
        <AIAvatar size={16} />
        <span className="c1" style={{ fontSize: 13 }}>
          AI 追问
        </span>
        <span className="t-meta">{timeCN(f.askedAt)}</span>
        <span className="flex1" />
        {!f.answer && ed.editable && (
          <Button variant="text" onClick={() => void ed.skip(f)} title="今天不再追问这个项目，明天照常">
            跳过
          </Button>
        )}
      </div>
      <div className="q">{f.question}</div>
      {f.answer ? (
        <div style={{ margin: '10px 0 4px 24px' }}>
          <div className="t-body" style={{ color: 'var(--text-1)' }}>
            {f.answer}
          </div>
          <div className="t-meta" style={{ marginTop: 2 }}>
            {f.answeredAt ? timeCN(f.answeredAt) : ''} 回复 · 已写入 {f.projectCode} 今天的进展
          </div>
        </div>
      ) : (
        ed.editable && <Choices f={f} ed={ed} onWrite={onWrite} writeLabel="都不是，我来写" />
      )}
    </div>
  );
}

function ManagerAsk({ f, ed, onWrite }: { f: FollowUpDTO; ed: ReportEditor; highlight: boolean; onWrite: () => void }) {
  const { data: projects = [] } = useProjects();
  const p = projects.find((x) => x.id === f.projectId);
  return (
    <div className="rp-ask top">
      <div className="row t-meta" style={{ gap: 8 }}>
        {p && <StatusIcon status={p.status} />}
        <span className="c2">
          {f.projectCode} {f.projectName}
        </span>
        {f.aboutDate && <span>· 关于你 {fmtMonthDay(f.aboutDate)} 的日报</span>}
      </div>
      <div style={{ marginTop: 12 }}>
        <Message avatar={<Avatar initial={f.fromInitial} seed={f.fromName} size={20} />} name={f.fromName} meta={`${timeCN(f.askedAt)}${f.ccNames.length ? ` · 抄送 ${f.ccNames.join('、')}` : ''}`}>
          {f.question}
        </Message>
      </div>
      <div style={{ marginLeft: 8 }}>
        <Choices f={f} ed={ed} onWrite={onWrite} writeLabel="自己写…" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Plans({ report, ed, adding, setAdding }: { report: ReportPageDTO; ed: ReportEditor; adding: boolean; setAdding: (v: boolean) => void }) {
  const [text, setText] = useState('');
  const [projectId, setProjectId] = useState<string>(report.items[0]?.projectId ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);
  const checkedCount = report.plans.filter((p) => p.checked).length;

  const add = async () => {
    if (!text.trim()) return setAdding(false);
    try {
      await ed.actions.addPlan.mutateAsync({ reportId: report.id, projectId: projectId || null, text });
      setText('');
    } catch (e) {
      ed.setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <GroupLabel
        right={
          <>
            <span>{report.plans.length}</span>
            <span className="flex1" />
            <span>{checkedCount > 5 ? '挑最重要的几项就好' : '勾选项明天会预填进日报'}</span>
          </>
        }
      >
        明日计划
      </GroupLabel>
      <div className="rp-rows">
        {report.plans.map((p) => (
          <div key={p.id} className={clsx('rp-row rp-plan', !p.checked && 'off')}>
            <button
              type="button"
              className={clsx('chk', p.checked && 'on')}
              aria-label={p.checked ? '取消勾选' : '勾选'}
              disabled={!ed.editable}
              onClick={() => ed.actions.updatePlan.mutate({ id: p.id, checked: !p.checked })}
            >
              {p.checked && <Icon name="check" size={10} color="var(--on-accent)" stroke={2} />}
            </button>
            <span className="t ellipsis">{p.text}</span>
            {p.suggestedByAI && <span className="t-meta">AI 建议</span>}
            {ed.editable && (
              <IconButton name="close" label="删除这项计划" className="del" onClick={() => ed.actions.deletePlan.mutate(p.id)} />
            )}
            <span className="pc">{p.projectCode ?? ''}</span>
          </div>
        ))}
        {ed.editable &&
          (adding ? (
            <div className="rp-row" style={{ background: 'var(--bg-hover)' }}>
              <Icon name="plus" />
              <input
                ref={inputRef}
                className="rp-plan-input"
                value={text}
                placeholder="明天要做的事…"
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    void add();
                  }
                  if (e.key === 'Escape') setAdding(false);
                }}
                onBlur={() => !text.trim() && setAdding(false)}
              />
              <select className="rp-select" value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="关联项目">
                <option value="">不关联项目</option>
                {report.items.map((i) => (
                  <option key={i.projectId} value={i.projectId}>
                    {i.projectCode}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <button type="button" className="rp-add" onClick={() => setAdding(true)}>
              <Icon name="plus" />
              <span>添加计划</span>
              <Kbd>P</Kbd>
            </button>
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PropertyPanel({ report, ed, isToday, showDeadline }: { report: ReportPageDTO; ed: ReportEditor; isToday: boolean; showDeadline: boolean }) {
  const doneCount = report.yesterdayPlans.filter((p) => p.state === 'done').length;
  const statusLabel = report.status === 'submitted' ? '已提交' : report.status === 'on_leave' ? '请假' : report.overdue ? '逾期' : '草稿';
  return (
    <aside className="rp-panel">
      <div>
        <KV k="状态">
          <StatusIcon status={report.status === 'submitted' ? 'done' : report.overdue ? 'blocked' : 'todo'} />
          <span className={report.overdue ? 'c-red' : undefined}>{statusLabel}</span>
        </KV>
        {showDeadline && report.status === 'draft' && isToday && ed.minutesLeft != null && (
          <KV k="截止">
            <span>{report.deadline}</span>
            <span className="t-meta">{ed.minutesLeft >= 0 ? `还有 ${durationText(ed.minutesLeft)}` : `已过 ${durationText(ed.minutesLeft)}`}</span>
          </KV>
        )}
        <KV k="提交至">
          <span className="ellipsis">{[report.submitTo.teamName, report.submitTo.managerName].filter(Boolean).join(' · ') || '—'}</span>
        </KV>
        <KV k="预填来源">
          <span>{report.prefillSource === 'plan' && report.prefillFromDate ? `${fmtMonthDay(report.prefillFromDate)} 明日计划` : '无'}</span>
        </KV>
      </div>

      {report.yesterdayPlans.length > 0 && (
        <div>
          <GroupLabel right={<><span className="flex1" /><span>{doneCount} / {report.yesterdayPlans.length} 完成</span></>}>上个工作日的计划</GroupLabel>
          {report.yesterdayPlans.map((p, i) => (
            <div key={i} className={clsx('plan-line', p.state === 'done' && 'done')}>
              <StatusIcon status={p.state === 'done' ? 'done' : p.state === 'explained' ? 'progress' : 'blocked'} />
              <span className="ellipsis">{p.text}</span>
            </div>
          ))}
          <div className="t-meta" style={{ marginTop: 6 }}>
            未完成的计划不会被追问，只要今日进展里写到了原因。
          </div>
        </div>
      )}

      {report.recent.length > 0 && (
        <div>
          <GroupLabel>最近日报</GroupLabel>
          {report.recent.map((r) => (
            <Link key={r.date} to={`/reports/${r.date}`} className="rp-recent">
              <span className="flex1">{fmtDateWeek(r.date)}</span>
              <span className="t-meta">{r.submittedAt ? timeCN(r.submittedAt) : r.status === 'on_leave' ? '请假' : '未提交'}</span>
            </Link>
          ))}
        </div>
      )}

      <div>
        <GroupLabel>谁会看到</GroupLabel>
        <div className="c3" style={{ fontSize: 12, lineHeight: 1.6 }}>
          提交后，{report.submitTo.managerName ?? '直属主管'}能看到你的日报、AI 追问和你的回复。草稿和修改记录只有你自己能看到。
        </div>
      </div>

      <div className="rp-hints">
        <Hint keys={['J', 'K']}>前一天 / 后一天</Hint>
        {ed.editable && <Hint keys={['E']}>编辑选中行</Hint>}
        {ed.editable && <Hint keys={['P']}>添加明日计划</Hint>}
        {ed.replyTarget && <Hint keys={['1']}>选择回复</Hint>}
      </div>
    </aside>
  );
}
